import React from 'react';
import { S1Hero, Button } from '@milanlaser/mlcomponents';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { processBackgroundAndGradientColorMap } from '../../utils/builder.utils';
import { FaGoogle, FaYelp } from 'react-icons/fa6';
import type { IconType } from 'react-icons';
import { getLocationContext } from '../../../../utils/general/getLocationContext';
import type { LocationBuilderState } from '../../../../utils/general/builder';
import { useFirebaseMetrics } from '../../utils/useFirebaseMetrics';
import { useBuilderImages } from '../../context/BuilderImageContext';
import { useOptimizedImage } from '../../../../hooks/useOptimizedImage';
import { createImageRenderer } from '../../../../utils/general/renderOptimizedImage';
import { Store } from 'clinics/clinics';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state?: BuilderState<T>;
    };
};

const iconMap: Record<string, IconType> = {
    Google: FaGoogle,
    Yelp: FaYelp,
};

type S1HeroArgs = React.ComponentProps<typeof S1Hero> &
    BuilderProps<{
        model: string;
        store?: Store;
    }> & {
        buttons?: Array<{
            text: string;
            variant: 'primary' | 'secondary' | 'cta';
            url: string;
            icon?: string;
        }>;
        imageURL?: string;
        heroTag?: 'h1' | 'h2';
        headTitle?: string;
        desktopPaddingTop?: PaddingSize;
        desktopPaddingBottom?: PaddingSize;
        mobilePaddingTop?: PaddingSize;
        mobilePaddingBottom?: PaddingSize;
    };

export const BuilderS1Hero = (props: S1HeroArgs) => {
    const { replaceVariables } = useFirebaseMetrics();
    const { getOptimizedImage } = useBuilderImages();
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',

        builderState,
        ...restProps
    } = props;

    const isStatePage = builderState.state?.model === 'state-page';
    const isClinicPage = builderState.state?.model === 'clinic-page';
    const locationName = getLocationContext(builderState as LocationBuilderState);

    const processedHeroTitle = replaceVariables(restProps.heroTitle);
    const headlineOverride =
        isStatePage || isClinicPage ? `Best Laser Hair Removal in ${locationName}` : processedHeroTitle;

    const processedHeroText = replaceVariables(restProps.heroText);
    const clinicCSAHeroText = isClinicPage && builderState.state?.store?.clinic_meta_text;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const dynamicImage = restProps.imageURL;

    // Check if we have an optimized version of this image
    const optimizedImageInfo = dynamicImage ? getOptimizedImage(dynamicImage) : null;
    const { gatsbyImageData, altText } = useOptimizedImage(
        optimizedImageInfo?.nodeId || '',
        optimizedImageInfo?.altText || restProps.imageAltText || processedHeroTitle
    );

    const backgroundColor = processBackgroundAndGradientColorMap({
        backgroundColor: props.backgroundColor || 'default',
        gradientColor: props.gradient || 'default',
    });

    const getIconComponent = (iconName: string) => {
        if (!iconName || !iconMap[iconName]) {
            return undefined;
        }
        const IconComponent = iconMap[iconName] as IconType;
        return <IconComponent />;
    };

    const renderImage = createImageRenderer({
        optimizedImageData: gatsbyImageData,
        optimizedAlt: altText,
        shouldUseOptimized: () => !!optimizedImageInfo && !!dynamicImage,
    });

    return (
        <div className={backgroundColor}>
            <S1Hero
                {...restProps}
                heroTitle={headlineOverride}
                heroText={clinicCSAHeroText || processedHeroText}
                key={`hero-${restProps.imageURL}`}
                className={`mcl-mx-auto mcl-px-4 md:mcl-px-6 xl:mcl-px-4 mcl-max-w-7xl ${topPaddingClasses} ${bottomPaddingClasses}`}
                renderImage={renderImage}
            >
                {(restProps.buttons?.length ?? 0) > 0 &&
                    restProps.buttons?.map((button, index) => (
                        <Button
                            key={`btn-${index}`}
                            variant={button.variant || 'primary'}
                            icon={getIconComponent(button.icon || '')}
                            onClick={() => (window.location.href = button.url)}
                        >
                            {button.text}
                        </Button>
                    ))}
            </S1Hero>
        </div>
    );
};
